import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { X, Check, XCircle, Loader2, Ban, ShieldCheck } from "lucide-react";

// Iter 123 + Iter 128 — Admin cancellation review. Admin enters the
// verified delivered wager PER CATEGORY (mirroring contract formation);
// server computes the weighted total = Σ(category × coefficient) using the
// SAME coefficient family and feeds it to the reused settlement pipeline.
// Approve runs settlement on that weighted number, releases the streamer's
// reservation minus 100% of the chat-unlock, marks contract `cancelled`.
// NO settlement_refund payout is created (avoids double-refund).

function ReviewModal({ row, onClose, onActed }) {
  const [proofs, setProofs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState({});
  const [coeffs, setCoeffs] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(null);
  const [rejOpen, setRejOpen] = useState(false);
  const [rejReason, setRejReason] = useState("");

  const vertical = row?.vertical || "casino";

  useEffect(() => {
    api.get(`/admin/cancellation-requests/${row.id}/proofs`)
      .then(({ data }) => setProofs(data))
      .catch((e) => toast.error(apiErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [row.id]);

  useEffect(() => {
    const url = vertical === "case_opening" ? "/config/case-coefficients" : "/config/wager-coefficients";
    api.get(url).then((r) => setCoeffs(r.data)).catch(() => setCoeffs({ categories: [] }));
  }, [vertical]);

  // Weighted delivered = Σ(category × coefficient) — same math as agreed_weighted.
  const weighted = useMemo(() => {
    if (!coeffs) return 0;
    return coeffs.categories.reduce((sum, c) => {
      const v = Number(cats[c.key]);
      if (!Number.isFinite(v)) return sum;
      return sum + v * Number(c.coefficient);
    }, 0);
  }, [cats, coeffs]);
  const hasAnyInput = coeffs && coeffs.categories.some((c) => {
    const v = cats[c.key];
    return v !== undefined && v !== "" && !Number.isNaN(Number(v));
  });

  const approve = async () => {
    if (!coeffs || !hasAnyInput) { toast.error("Enter delivered wager per category"); return; }
    const payload = {};
    for (const c of coeffs.categories) {
      const raw = cats[c.key];
      const v = Number(raw === "" || raw === undefined ? 0 : raw);
      if (!Number.isFinite(v) || v < 0) { toast.error(`${c.key}: must be ≥ 0`); return; }
      payload[c.key] = v;
    }
    setBusy("approve");
    try {
      await api.post(`/admin/cancellation-requests/${row.id}/approve`, { deliveredCategories: payload, note });
      toast.success("Cancellation approved — contract cancelled, reservation resolved.");
      onActed && onActed(); onClose();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(null); }
  };
  const reject = async () => {
    if (!rejReason.trim()) { toast.error("Reason required"); return; }
    setBusy("reject");
    try {
      await api.post(`/admin/cancellation-requests/${row.id}/reject`, { reason: rejReason.trim() });
      toast.success("Rejected — contract returned to active.");
      onActed && onActed(); onClose();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(null); }
  };

  const reason = (row.cancellationRequest || {}).reason || proofs?.reason || "";

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-cxl-modal">
      <div className="w-full max-w-2xl bg-[#0B1A14] border border-[#F26D6D]/40 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 sticky top-0 bg-[#0B1A14]">
          <div className="flex items-center gap-2"><Ban className="w-4 h-4 text-[#F26D6D]" /><h3 className="font-display font-bold text-base">Cancellation · {row.player_alias}</h3></div>
          <button onClick={onClose} className="text-[#8B97AE] hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="border border-white/10 p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841] mb-1">Reason</div>
            <div className="text-xs text-[#E8ECF4] whitespace-pre-wrap">{reason || "—"}</div>
          </div>
          <div className="border border-white/10 p-3">
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7] mb-2"><ShieldCheck className="w-3 h-3" />Wager-delivered proof</div>
            {loading ? <div className="text-[10px] text-[#8B97AE] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Loading…</div>
              : proofs?.files?.length ? (
                <ul className="space-y-2">
                  {proofs.files.map((f) => (
                    <li key={f.id} className="border border-white/10 p-2">
                      <div className="flex items-center justify-between text-[11px] font-mono">
                        <span className="text-[#E8ECF4] truncate mr-2">{f.filename}</span>
                        {f.url && <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-[10px] uppercase tracking-widest text-[#F5B841]">Open</a>}
                      </div>
                      {f.url && (f.contentType || "").startsWith("image/") && <img src={f.url} alt="" className="max-h-64 object-contain border border-white/10 mt-1" />}
                    </li>
                  ))}
                </ul>
              ) : <div className="text-[11px] font-mono uppercase tracking-widest text-[#F26D6D]">no proofs</div>}
          </div>
          <div className="border border-[#2DD4A7]/40 bg-[#2DD4A7]/[0.04] p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7] mb-2">Enter verified delivered wager · per category</div>
            {!coeffs ? (
              <div className="text-[10px] text-[#8B97AE]">Loading coefficients…</div>
            ) : coeffs.categories.length === 0 ? (
              <div className="text-[10px] text-[#F26D6D]">No coefficient config for vertical {vertical}.</div>
            ) : (
              <div className="space-y-1" data-testid="admin-cxl-category-rows">
                <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)] gap-x-3 text-[10px] font-mono text-[#8B97AE] uppercase tracking-widest pb-1 border-b border-white/10">
                  <span>Category</span>
                  <span className="text-right">Delivered</span>
                  <span className="text-right">Weighted (× coef)</span>
                </div>
                {coeffs.categories.map((c) => {
                  const raw = cats[c.key];
                  const v = Number(raw === "" || raw === undefined ? 0 : raw);
                  const w = Number.isFinite(v) ? v * Number(c.coefficient) : 0;
                  return (
                    <div key={c.key} className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)] gap-x-3 items-center py-0.5 text-xs font-mono">
                      <span className="text-[#E8ECF4] capitalize">{c.key.replace(/_/g, " ")}</span>
                      <input
                        data-testid={`admin-cxl-delivered-${c.key}`}
                        type="number" step="0.01" min="0"
                        value={cats[c.key] ?? ""}
                        onChange={(e) => setCats((old) => ({ ...old, [c.key]: e.target.value }))}
                        className="rounded-none bg-transparent border border-white/20 h-8 font-mono tabular-nums text-right px-2 text-xs w-full"
                      />
                      <span className="text-right tabular-nums text-[#8B97AE]">{w.toLocaleString(undefined, { maximumFractionDigits: 2 })} <span className="text-[9px]">× {c.coefficient}</span></span>
                    </div>
                  );
                })}
                <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)] gap-x-3 mt-1 pt-1 border-t border-white/10 text-xs font-mono">
                  <span className="text-[10px] uppercase tracking-widest text-[#8B97AE]">Delivered weighted total</span>
                  <span></span>
                  <span className="text-right tabular-nums text-[#2DD4A7]" data-testid="admin-cxl-weighted-total">{weighted.toLocaleString(undefined, { maximumFractionDigits: 2 })} USD</span>
                </div>
              </div>
            )}
            <textarea data-testid="admin-cxl-note" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note visible in audit log"
              className="w-full mt-2 bg-transparent border border-white/20 text-xs text-[#E8ECF4] p-2 font-mono outline-none resize-none" />
            <div className="mt-2 text-[10px] text-[#8B97AE] font-mono uppercase tracking-widest">Weighted delivered = Σ(category × coefficient), same coefficients as formation. Approval runs settlement on the weighted total, releases streamer reservation minus 100% of chat-unlock, marks contract cancelled. No settlement_refund payout is created.</div>
          </div>
          <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
            <button data-testid="admin-cxl-approve" onClick={approve} disabled={busy !== null || !hasAnyInput}
              className="inline-flex items-center gap-1.5 px-4 h-9 bg-[#2DD4A7] text-[#04150F] text-xs font-mono uppercase tracking-widest hover:bg-[#F5B841] disabled:opacity-40">
              <Check className="w-3 h-3" />{busy === "approve" ? "…" : "Approve — cancel contract"}
            </button>
            <button data-testid="admin-cxl-reject-open" onClick={() => setRejOpen(!rejOpen)} disabled={busy !== null}
              className="inline-flex items-center gap-1.5 px-4 h-9 border border-[#F26D6D]/60 text-[#F26D6D] text-xs font-mono uppercase tracking-widest hover:bg-[#F26D6D]/[0.08]">
              <XCircle className="w-3 h-3" />Reject
            </button>
          </div>
          {rejOpen && (
            <div className="border border-[#F26D6D]/40 bg-[#F26D6D]/[0.06] p-3 space-y-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#F26D6D]">Reason (required)</div>
              <textarea data-testid="admin-cxl-reject-reason" value={rejReason} onChange={(e) => setRejReason(e.target.value.slice(0, 1000))}
                rows={3} placeholder="Explain why…"
                className="w-full bg-transparent border border-white/20 text-xs text-[#E8ECF4] p-2 font-mono outline-none resize-none" />
              <div className="flex justify-end gap-2">
                <button onClick={() => { setRejOpen(false); setRejReason(""); }} className="px-3 h-8 border border-white/20 text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Cancel</button>
                <button data-testid="admin-cxl-reject-confirm" onClick={reject} disabled={busy !== null || !rejReason.trim()}
                  className="px-3 h-8 bg-[#F26D6D] text-white text-[10px] font-mono uppercase tracking-widest disabled:opacity-40">{busy === "reject" ? "…" : "Reject & notify"}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminCancellations({ onAction } = {}) {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cancellation-requests");
      setRows(data || []);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const handleActed = useCallback(() => { load(); onAction && onAction(); }, [load, onAction]);
  const openRow = openId ? rows.find((r) => r.id === openId) : null;
  return (
    <section data-testid="admin-cancellations" className="space-y-4">
      {loading ? (
        <div className="text-xs text-[#8B97AE] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-[#8B97AE] border border-white/10 p-6 text-center">No pending cancellation requests.</div>
      ) : (
        <div className="border border-white/10">
          <table className="w-full text-sm">
            <thead className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
              <tr className="border-b border-white/10 bg-white/[0.02]">
                <th className="text-left p-2">Player</th>
                <th className="text-left p-2">Casino</th>
                <th className="text-right p-2">Agreed</th>
                <th className="text-left p-2">Reason (excerpt)</th>
                <th className="text-left p-2">Requested</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`admin-cxl-row-${r.id}`} onClick={() => setOpenId(r.id)} className="border-b border-white/5 last:border-b-0 cursor-pointer hover:bg-white/[0.03]">
                  <td className="p-2 font-mono text-xs">{r.player_alias}</td>
                  <td className="p-2 font-mono text-xs">{r.casino || "—"}</td>
                  <td className="p-2 font-mono text-xs text-right text-[#F5B841]">{Number(r.agreedWeightedWager || r.agreed_monthly_volume || 0).toLocaleString()}</td>
                  <td className="p-2 font-mono text-[11px] text-[#E8ECF4] truncate max-w-[300px]">{((r.cancellationRequest || {}).reason || "").slice(0, 80)}</td>
                  <td className="p-2 font-mono text-xs text-[#8B97AE]">{((r.cancellationRequest || {}).requestedAt || "").slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {openRow && <ReviewModal row={openRow} onClose={() => setOpenId(null)} onActed={handleActed} />}
    </section>
  );
}
